import { Box } from '@mui/material'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import { useTreeData, useTreeExpansion, useTreeSelection } from './hooks'
import { TreeNode } from './TreeNode'
import { TreeSkeleton } from './TreeSkeleton'
import { DropPosition, StructureTreeItem, TreeV2Handle, TreeV2Props } from './types'

export const TreeV2 = forwardRef<TreeV2Handle, TreeV2Props>(
	(
		{
			onItemClick,
			onItemDoubleClick,
			onItemContextMenu,
			selectedItemId,
			expandedItems: controlledExpandedItems,
			onExpandedItemsChange,
			onSelectedItemsChange,
			iconComponent,
			actionComponent,
			className,
			autoExpandDepth = 0,
			getDragDropHandlers,
			onBatchLoaded,
			onChildrenLoaded,
			selectMode = false,
			checkedItemIds = [],
			onCheckedItemsChange,
			alphabetSort = true,
			initialChildrenMap,
			initialSystems,
			initialExpandedIds,
			onFullTreeLoadingChange,
			selectedSystemId = null,
			selectedBranchId = null,
		},
		ref,
	) => {
		const {
			systems,
			childrenMap,
			loadingIds,
			pendingRefreshParentIds,
			isTreeLoading,
			isFullTreeLoading,
			loadChildren,
			refresh,
			refreshSystems,
			refreshSystemsSilent,
			revealItem,
			findItem,
			setChildrenMap,
			setRawSystems,
			setUsedIds,
		} = useTreeData({
			onChildrenLoaded,
			onBatchLoaded,
			alphabetSort,
			initialChildrenMap,
			initialSystems,
			initialExpandedIds,
			selectedSystemId,
			selectedBranchId,
		})

		// Real system IDs (not display roots) — used when collapsing a system
		const systemIds = useMemo(() => {
			if (selectedSystemId) return new Set([selectedSystemId])
			return new Set(systems.map((s) => s.id))
		}, [selectedSystemId, systems])

		// Ensure selected system children are loaded (system node itself is not rendered)
		useEffect(() => {
			if (!selectedSystemId) return
			void loadChildren(selectedSystemId)
		}, [loadChildren, selectedSystemId])

		// Expansion management
		const {
			allowCollapseRef,
			expandedItems,
			handleExpandedItemsChange,
			setInternalExpandedItems,
		} = useTreeExpansion({
			controlledExpandedItems,
			onExpandedItemsChange,
			autoExpandDepth,
			systems,
			systemIds,
			childrenMap,
			loadChildren,
		})

		// Selection management
		const { handleCheckChange, isIndeterminate } = useTreeSelection({
			checkedItemIds,
			onCheckedItemsChange,
			onChildrenLoaded,
			childrenMap,
			setChildrenMap,
			setUsedIds,
			selectedBranchId,
		})

		// Drop target state
		const [dropTarget, setDropTarget] = useState<{
			id: string
			position: DropPosition
		} | null>(null)

		const updateNodes = useCallback(
			(nodeIds: number[], updates: Partial<Pick<StructureTreeItem, 'lockedFor' | 'lockedTime' | 'status'>>) => {
				const nodeIdSet = new Set(nodeIds.map(String))
				setRawSystems((prev) =>
					prev.map((system) =>
						nodeIdSet.has(system.id) ? { ...system, ...updates } : system,
					),
				)
				setChildrenMap((prev) => {
					const updated = { ...prev }
					Object.entries(updated).forEach(([parentId, children]) => {
						const hasUpdates = children.some((c) => nodeIdSet.has(c.id))
						if (hasUpdates) {
							updated[parentId] = children.map((child) =>
								nodeIdSet.has(child.id) ? { ...child, ...updates } : child,
							)
						}
					})
					return updated
				})
			},
			[setChildrenMap, setRawSystems],
		)

		// Single source of truth: notify parent when full-tree loading changes (avoids 500ms polling flicker)
		useEffect(() => {
			onFullTreeLoadingChange?.(isFullTreeLoading)
		}, [isFullTreeLoading, onFullTreeLoadingChange])

		useImperativeHandle(
			ref,
			() => ({
				isFullTreeLoading,
				revealItem: (itemId: string) =>
					revealItem(itemId, expandedItems, (newExpanded) => {
						if (onExpandedItemsChange) {
							onExpandedItemsChange(newExpanded)
						} else {
							setInternalExpandedItems(newExpanded)
						}
					}),
				refresh: () => refresh(expandedItems),
				refreshParent: (parentId: string) => loadChildren(parentId, true),
				refreshParentSilent: (parentId: string) => loadChildren(parentId, true, true),
				refreshSystems,
				refreshSystemsSilent,
				updateNodes,
			}),
			[revealItem, refresh, loadChildren, refreshSystems, refreshSystemsSilent, expandedItems, onExpandedItemsChange, setInternalExpandedItems, updateNodes, isFullTreeLoading],
		)

		// Handle selection
		const handleSelectedItemsChange = useCallback(
			(_event: React.SyntheticEvent | null, itemId: string | null) => {
				onSelectedItemsChange?.(itemId)
			},
			[onSelectedItemsChange],
		)

		// Handle item click from MUI
		const handleItemClick = useCallback(
			(event: React.SyntheticEvent, itemId: string) => {
				// Check if click was on arrow icon
				const target = event.target as HTMLElement
				const isArrowClick = target.closest('.MuiTreeItem-iconContainer') !== null

				if (isArrowClick) {
					// Arrow clicks only expand/collapse, don't trigger onItemClick
					allowCollapseRef.current = true
					return
				}

				// Non-arrow clicks should not expand/collapse
				allowCollapseRef.current = false

				const item = findItem(itemId)
				if (item) {
					onItemClick?.(item)
				}
			},
			[findItem, onItemClick, allowCollapseRef],
		)

		// Handle row click (no longer needed for expansion tracking, just for stopPropagation)
		const handleRowClick = useCallback(
			(_item: StructureTreeItem, _isExpanded: boolean, _e: React.MouseEvent) => {
				// No-op - expansion is now handled entirely in handleItemClick
			},
			[],
		)

		// Handle row double-click - triggers callback AND toggles expand/collapse
		const handleRowDoubleClick = useCallback(
			(
				item: StructureTreeItem,
				isExpanded: boolean,
				isContainer: boolean,
				e: React.MouseEvent,
			) => {
				e.stopPropagation()

				const target = e.target as HTMLElement
				const isArrowClick =
					target.closest('.MuiTreeItem-iconContainer') !== null

				if (!isArrowClick) {
					onItemDoubleClick?.(item)

					if (isContainer) {
						allowCollapseRef.current = true
						const newExpandedItems = isExpanded
							? expandedItems.filter((id) => id !== item.id)
							: [...expandedItems, item.id]
						handleExpandedItemsChange(null, newExpandedItems)
					}
				}
			},
			[onItemDoubleClick, expandedItems, handleExpandedItemsChange, allowCollapseRef],
		)

		// Handle context menu
		const handleContextMenu = useCallback(
			(item: StructureTreeItem, e: React.MouseEvent) => {
				onItemContextMenu?.(item, e)
			},
			[onItemContextMenu],
		)

		// Compute which items have children from the childrenMap directly
		const parentIdsWithChildren = useMemo(
			() => new Set(Object.entries(childrenMap).filter(([, c]) => c.length > 0).map(([id]) => id)),
			[childrenMap]
		)

		// Skeleton only on initial load (no data yet); on refresh we keep showing the tree
		if ((isTreeLoading || isFullTreeLoading) && systems.length === 0) {
			return (
				<Box sx={{ height: '100%', minHeight: 200 }}>
					<TreeSkeleton />
				</Box>
			)
		}

		return (
			<SimpleTreeView
				data-testid="tree-v2"
				className={className}
				expandedItems={expandedItems}
				onExpandedItemsChange={handleExpandedItemsChange}
				selectedItems={selectedItemId}
				onSelectedItemsChange={handleSelectedItemsChange}
				onItemClick={handleItemClick}
				sx={{
					maxHeight: '100%',
					overflow: 'auto',
				}}
			>
				{systems.map((system) => (
					<TreeNode
						key={system.id}
						item={system}
						expandedItems={expandedItems}
						loadingIds={loadingIds}
						pendingRefreshParentIds={pendingRefreshParentIds}
						childrenMap={childrenMap}
						parentIdsWithChildren={parentIdsWithChildren}
						iconComponent={iconComponent}
						actionComponent={actionComponent}
						selectMode={selectMode}
						checkedItemIds={checkedItemIds}
						isCheckboxDisabled={isFullTreeLoading}
						dropTarget={dropTarget}
						getDragDropHandlers={getDragDropHandlers}
						onRowClick={handleRowClick}
						onRowDoubleClick={handleRowDoubleClick}
						onContextMenu={handleContextMenu}
						onCheckChange={handleCheckChange}
						setDropTarget={setDropTarget}
						isIndeterminate={isIndeterminate}
					/>
				))}
			</SimpleTreeView>
		)
	},
)

TreeV2.displayName = 'TreeV2'
